/**
 * Content for the multi-box concept case study.
 *
 * Kept out of MultiBox.js so the page component stays readable — the
 * deep dive alone is 40 rows. Same split rationale as the metric and
 * pipeline arrays at the top of Ballys.js, just large enough to earn
 * its own file.
 */

/* ─── Where it breaks across the chain ─────────────────────────── */
export const CHAIN = [
  {
    step: 'Listing',
    breaks:
      'The product record has one size and one weight, so there’s no way to say “ships in 3 boxes.” Fees and storage get planned for a box that doesn’t exist.',
    fix: 'Let the seller describe each box (what’s inside, size, weight, where it ships from) under the same product.',
  },
  {
    step: 'Labeling',
    breaks:
      'If all three boxes carry the product’s barcode, the warehouse counts three treadmills. If only one does, the other two show up as mystery boxes.',
    fix: 'Give every carton its own ID that names its box type, plus a big “Box 2 of 3” a person can read from across the dock.',
  },
  {
    step: 'Inbound',
    breaks:
      'Boxes from the same treadmill get split across pallets, trucks and even suppliers, and the shipment plan only counts treadmills.',
    fix: 'Plan and track shipments in boxes, so 20 treadmills means 60 cartons expected, across every source.',
  },
  {
    step: 'Receiving',
    breaks:
      'Scanning a box either adds a whole treadmill to stock or gets rejected. Nothing knows whether a set is complete.',
    fix: 'Count boxes by type, and hold boxes from incomplete sets so customers never see them as available.',
  },
  {
    step: 'Pick and pack',
    breaks:
      'The three boxes can end up in different places, and one missing box blocks the whole order.',
    fix: 'In the pilot, a treadmill only counts as sellable when all of its box types are in the same building.',
  },
  {
    step: 'Returns',
    breaks:
      'Two of three boxes come back, and suddenly the refund, the resale and who’s responsible are all unclear.',
    fix: 'Track returns by carton, so complete returns go back on the shelf and partial ones go to a person to decide.',
  },
];

/* ─── Decision log ─────────────────────────────────────────────── */
export const DECISIONS = [
  {
    question: 'Where do we start?',
    choseIndex: 0,
    considered: ['The box label', 'Receiving', 'The customer'],
    chose: 'The box label, so the deep dive follows the seller’s journey from setup to arrival at the warehouse.',
    why: 'Receiving, picking and returns all read the same labels. Starting at receiving would only catch mistakes faster, and starting at the customer would patch symptoms of problems that begin upstream.',
    tradeoff: 'Customers never see this work, so the payoff is indirect at first.',
  },
  {
    question: 'How is each box identified?',
    choseIndex: 2,
    considered: ['One barcode on every box', 'Matched, numbered sets', 'Interchangeable box types with unique carton IDs'],
    chose: 'Interchangeable box types, with a unique ID on every carton.',
    why: 'The unique ID tracks each physical carton for damage claims and returns. The box type inside the ID completes sets, so any Box 2 completes any treadmill. Sellers print labels by box type, and one crushed box never strands two good ones.',
    tradeoff: 'It doesn’t fit parts paired at the factory, like sofa fabric, so matched sets stay available as an opt-in.',
  },
  {
    question: 'Where can a set’s boxes be?',
    choseIndex: 0,
    considered: ['One building', 'Anywhere in the network'],
    chose: 'One building for the pilot. Plans with more than one source send every shipment to the same building.',
    why: 'All boxes ship together from one place, so delivery barely changes, and any problem stays contained to a building that’s easy to switch off.',
    tradeoff: 'Treadmills become sellable later than they could. Shipping boxes from different buildings is faster and more flexible, but it brings split deliveries and messier returns, so it’s phase 2.',
  },
  {
    question: 'Where do people see what’s happening?',
    choseIndex: 1,
    considered: ['A new tracker everyone logs into', 'One shared carton timeline inside existing tools'],
    chose: 'One shared carton timeline, shown inside the seller portal, the scanner app, the operations dashboard and the service console.',
    why: 'Associates won’t put down their scanner, and sellers already live in their seller portal. Because everyone reads the same timeline, status never disagrees between teams.',
    tradeoff: 'More teams have to coordinate, and the experience can drift between tools without shared design rules.',
  },
  {
    question: 'Where do a set’s boxes live inside the building?',
    choseIndex: 0,
    considered: ['Together in a multi-box zone', 'Wherever each box fits', 'Anywhere while waiting, moved together once complete'],
    chose: 'Together, in a dedicated multi-box zone, for the pilot.',
    why: 'Picking stays simple and nothing gets left behind, which matters most while the pilot is still learning.',
    tradeoff: 'Waiting boxes take up space, so the 80% space alert (edge case 17) is the guardrail. If volume grows, moving boxes together once a set is complete is the next layout to test.',
  },
];

/* ─── The model ────────────────────────────────────────────────── */
export const BOX_TYPES = [
  { id: 'B1', name: 'Deck, belt and motor', weight: '182 lb', received: 20 },
  { id: 'B2', name: 'Console and uprights', weight: '58 lb', received: 20 },
  { id: 'B3', name: 'Side rails and hardware', weight: '26 lb', received: 18 },
];

/* ─── Pilot one-pager ──────────────────────────────────────────── */
export const ONE_PAGER = [
  {
    heading: 'Problem',
    body: 'The network assumes one product, one barcode, one package. Products that ship in several boxes (fitness equipment, furniture) either get forced into that rule or handled by hand, which shows up as phantom stock, stranded boxes, split shipments and incomplete returns.',
  },
  {
    heading: 'Proposal',
    body: 'Keep one listing and one price. Under it, each box type gets its own size, weight and source, and every carton gets a unique ID that names its box type. Each building counts boxes by type, and sellable units equal the lowest count. Boxes from incomplete sets are held and never shown as available. Every label, notice and scan writes to one carton timeline that each team sees inside its own tool.',
  },
  {
    heading: 'Pilot scope',
    list: [
      '8 weeks in one large-item building',
      'Home fitness equipment that ships in 2 or 3 boxes',
      'About 25 sellers who opt in, from one or more sources',
      'Plans with more than one source route every shipment to the same building',
      'Each set’s boxes are stored together in a multi-box zone',
      'Single-box products don’t change at all',
    ],
  },
  {
    heading: 'Guardrails',
    list: [
      'Incomplete sets are never shown as available to buy.',
      'Held boxes trigger a seller prompt on day 3 and a decision on day 14: send the missing box or get the extras back.',
      'Each box is weighed at the dock against its box type. Anything more than 10% off goes to a problem-solve station.',
      'One switch pauses new multi-box shipments. Boxes already in the building finish through today’s manual process.',
      'Stop adding sellers if incomplete-delivery contacts run above the single-box rate two weeks in a row.',
    ],
  },
  {
    heading: 'How we’d know it works',
    list: [
      'Set-complete rate: the share of sellable units that are complete within 48 hours of their first box being received.',
      'Incomplete-delivery contacts per 1,000 multi-box orders.',
      'Return completeness: the share of returns that come back with every box.',
      'Counter-metric: receiving time per box compared with single-box items. The dock shouldn’t get slower.',
    ],
    note: 'Targets get set after a two-week baseline instead of being guessed up front.',
  },
  {
    heading: 'Where AI helps, where people decide',
    split: {
      ai: [
        'Pre-fills box count, sizes and weights from spec sheets and catalog data, and the seller confirms each box.',
        'Flags anomalies: weights that don’t match the box template, box types whose counts never pair up, and the same product listed twice (once as one box, once as three).',
        'Finds existing listings that are probably multi-box, so the pilot invites the right sellers.',
      ],
      people: [
        'What happens to a damaged box, who pays when a return comes back incomplete, when a product needs matched sets, and whether customers are okay with split deliveries.',
      ],
    },
  },
];

/* ─── PRD header ───────────────────────────────────────────────────
   Atlassian's first PRD section is "project specifics": participants,
   status, target release. Every value here is restated from elsewhere
   on the page, not newly asserted. */
export const PRD_META = [
  { label: 'Role', value: 'Product management and product design, solo concept' },
  { label: 'Status', value: 'Concept — not built' },            // "If I were pitching this to a team"
  { label: 'Proposed scope', value: '8-week pilot, one building' }, // one-pager, Pilot scope
  { label: 'Date', value: 'One evening, October 2026' },
];

/* The old Overview section duplicated Role and Date once PRD_META
   existed, so its one unique field moved here and the section went. */
export const PRD_DELIVERABLE =
  'Problem framing, a decision log, 40 acceptance criteria across three journeys, and a pilot plan';

/* ─── Assumptions ──────────────────────────────────────────────────
   Atlassian section 4. Each of these is a belief the proposal rests on
   that is stated elsewhere on this page; the comment names where. */
export const ASSUMPTIONS = [
  {
    text: 'Boxes of the same type are interchangeable: any Box 2 completes any treadmill of that model and version.',
    basis: 'The whole counting model depends on it, and it is what makes one crushed box stop stranding two good ones.',
  }, // Decision 2
  {
    text: 'Most multi-box products don’t need parts matched at the factory.',
    basis: 'Products that do, like sofa fabric from different dye batches, need matched sets, which stay an opt-in rather than the default.',
  }, // Decision 2 trade-off
  {
    text: 'Routing every shipment in a plan to one building is an acceptable cost for the pilot.',
    basis: 'All boxes then ship together from one place, so delivery barely changes and any problem stays contained to a building that is easy to switch off.',
  }, // Decision 3
  {
    text: 'Spec-sheet data is good enough to pre-fill box counts, sizes and weights, with the seller confirming each box.',
    basis: 'A wrong weight here causes problems all the way down the line, so the seller confirms rather than accepts.',
  }, // Screen 1 lede + "Where AI helps"
  {
    text: 'The thresholds are roughly right but not yet proven: 5% weight drift, 50 lb two-person lift, 14-day hold, 80% space alert.',
    basis: 'These are starting points to tune during the pilot, not settled numbers.',
  }, // THRESHOLD_NOTE
];

/* ─── What we're not doing ─────────────────────────────────────────
   Atlassian section 8. Every item is a deferral this page already
   makes explicitly; the comment names where. */
export const NON_GOALS = [
  {
    text: 'Changing anything for single-box products.',
    note: 'They are the overwhelming majority and carry none of this complexity.',
  }, // Goal statement + Pilot scope
  {
    text: 'Completing a set from boxes held in different buildings.',
    note: 'Faster and more flexible, but it brings split deliveries and messier returns. Phase 2.',
  }, // Decision 3 trade-off
  {
    text: 'Matched sets, where specific boxes must ship together.',
    note: 'Stays available as an opt-in for products like sofas, rather than being the model the pilot proves.',
  }, // Decision 2 trade-off
  {
    text: 'Moving a set’s boxes together once it completes, instead of storing them together throughout.',
    note: 'The next storage layout worth testing if volume grows, but not what the pilot is measuring.',
  }, // Decision 5 trade-off
  {
    text: 'A separate tracker for multi-box work.',
    note: 'The carton timeline shows up inside the seller portal, scanner, operations dashboard and service console instead.',
  }, // Decision 4
];

/* ─── Open questions ───────────────────────────────────────────────
   Atlassian section 7, "things we need to decide or research". The
   first four are verbatim the decisions this page hands to people
   rather than to the system. */
export const OPEN_QUESTIONS = [
  { q: 'What happens to a damaged box?', kind: 'Decide' },
  { q: 'Who pays when a return comes back incomplete?', kind: 'Decide' },
  { q: 'When does a product need matched sets?', kind: 'Decide' },
  { q: 'Are customers okay with split deliveries?', kind: 'Research' },
  { q: 'Do the 5% / 50 lb / 14-day / 80% thresholds hold up against real volume?', kind: 'Research' },
  { q: 'Does the operations view — waiting sets by age, zone fill, exceptions queue — change what the pilot needs?', kind: 'Design' },
];
